import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useApp } from "../../context/AppContext";
import bonafideService from "../../services/bonafideService";
import Pagination from "../../components/common/Pagination";
import ConfirmModal from "../../components/common/ConfirmModal";

export default function BonafideList() {
  const navigate = useNavigate();
  const { academicYear, isClerk } = useApp();
  const basePath = isClerk ? "/clerk/bonafide" : "/bonafide";

  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await bonafideService.getAll({
        academicYear: academicYear?._id || "",
        search, page, limit: 15,
      });
      setRecords(res.data);
      setPagination(res.pagination);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [academicYear, search, page]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);
  useEffect(() => { setPage(1); }, [search, academicYear]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await bonafideService.remove(deleteTarget._id);
      toast.success("Certificate deleted");
      setDeleteTarget(null);
      fetchRecords();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Bonafide Certificates</h1>
          <p className="page-subtitle">
            {academicYear
              ? `${pagination?.total ?? "…"} certificates — ${academicYear.label}`
              : "No academic year selected"}
          </p>
        </div>
        <Link to={`${basePath}/new`} className="btn btn-primary">+ Issue Certificate</Link>
      </div>

      {!academicYear && (
        <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "0.75rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#92400e" }}>
          ⚠️ Select an academic year from the header to view certificates.
        </div>
      )}

      <div className="card">
        <div style={{ padding: "0.75rem 1rem", borderBottom: "1px solid var(--border)", display: "flex", gap: "0.75rem" }}>
          <div className="search-bar">
            <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ color: "#9ca3af", flexShrink: 0 }}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              placeholder="Search by student name or Sr. No..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="loading-center"><div className="spinner" />Loading...</div>
        ) : records.length === 0 ? (
          <div className="empty-state">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>{search ? "No certificates match your search." : "No certificates issued this year."}</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Student Name</th>
                  <th>Class</th>
                  <th>Session</th>
                  <th>Date</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={r._id}>
                    <td><span className="badge badge-blue">{r.srNo}</span></td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{r.student?.fullName}</div>
                      <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>{r.student?.enrollmentNo}</div>
                    </td>
                    <td>{r.className}</td>
                    <td>{r.sessionStart}–{r.sessionEnd}</td>
                    <td>{r.certificateDate}</td>
                    <td>
                      <div style={{ display: "flex", gap: "0.4rem", justifyContent: "flex-end" }}>
                        <button className="btn btn-success btn-sm" onClick={() => navigate(`${basePath}/${r._id}/print`)}>🖨 Print</button>
                        <button className="btn btn-outline btn-sm" onClick={() => navigate(`${basePath}/${r._id}/edit`)}>Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(r)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination pagination={pagination} onPageChange={setPage} />
      </div>

      {deleteTarget && (
        <ConfirmModal
          title="Delete Certificate"
          message={`Delete Bonafide Sr. No. ${deleteTarget.srNo} for ${deleteTarget.student?.name}?`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
          loading={deleting}
        />
      )}
    </div>
  );
}